import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Navigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { AuthLayout, BackToSignInLink } from '@/components/layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { accountApi } from '@/resources/account';
import { resetPasswordSchema, passwordRules } from '@/schemas';
import { cn } from '@/lib/utils';
import { RoutePath } from '@/routes';
import { ApiError } from '@/services/api.service';
import { handleApiError } from '@/utils';

// AUT-39: components/account.md § The J5 path is a redirect, not a screen --
// identical copy for every dead-link cause (no token, {valid:false}, a
// failed verify, and a 422 on errors.token at submit), so the screen never
// confirms that a token existed.
const DEAD_LINK_MESSAGE = 'This link can no longer be used. Ask for a new one below.';

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

function RequirementItem({ met, label, testId }: { met: boolean; label: string; testId: string }) {
  return (
    <div className="flex items-center gap-2" data-testid={testId} data-met={met}>
      {met ? (
        <CheckCircle2 className="h-4 w-4 text-trend-positive shrink-0" />
      ) : (
        <XCircle className="h-4 w-4 text-muted-foreground shrink-0" />
      )}
      <span className={cn('text-xs', met ? 'text-foreground' : 'text-muted-foreground')}>{label}</span>
    </div>
  );
}

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // A token that dies between page load and submit (a 422 on errors.token,
  // account.api.ts § useResetPassword has no hook-level onError for exactly
  // this branching) takes the same dead-link path as the other three causes.
  const [tokenDiedAtSubmit, setTokenDiedAtSubmit] = useState(false);

  const { data, isError, isLoading } = accountApi.useVerifyResetToken(token);

  // isLoading (isPending && isFetching) is only true while a token is being
  // verified -- with no token the query is disabled and isLoading is false,
  // so the !token branch below fires immediately instead of waiting on it.
  const isDead = !token || isError || data?.valid === false || tokenDiedAtSubmit;

  useEffect(() => {
    if (isDead) {
      toast.error(DEAD_LINK_MESSAGE);
    }
  }, [isDead]);

  const { mutate: resetPassword, isPending } = accountApi.useResetPassword();

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', password_confirmation: '' },
  });

  const passwordValue = watch('password');

  const onSubmit = (formValues: ResetPasswordFormValues) => {
    if (!token) return;

    resetPassword(
      {
        token,
        password: formValues.password,
        password_confirmation: formValues.password_confirmation,
      },
      {
        onError: (error) => {
          if (error instanceof ApiError && error.status === 422) {
            const body = error.data as { errors?: Record<string, string[]> } | undefined;
            if (body?.errors?.token) {
              setTokenDiedAtSubmit(true);
              return;
            }
            if (body?.errors?.password) {
              setError('password', { message: body.errors.password[0] });
              return;
            }
          }
          handleApiError(error);
        },
      },
    );
  };

  if (isDead) {
    return <Navigate to={RoutePath.ForgotPassword} replace />;
  }

  if (isLoading) {
    return (
      <AuthLayout variant="centered">
        <BackToSignInLink />

        <div className="flex flex-col items-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">Checking your reset link…</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout variant="centered">
      <BackToSignInLink />

      <h2 className="text-2xl font-semibold text-foreground">Set a new password</h2>
      <p className="mt-2 text-sm text-muted-foreground">Choose a new password for your Signal account.</p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6" noValidate>
        <div className="w-full space-y-2">
          <Label
            htmlFor="password"
            className={cn('block text-sm leading-none', errors.password && 'text-destructive')}
          >
            New password
          </Label>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            className={cn(errors.password && 'border-destructive focus-visible:ring-destructive')}
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? 'password-error' : undefined}
            {...register('password')}
          />
          {errors.password && (
            <p id="password-error" className="leading-none text-destructive text-sm">
              {errors.password.message}
            </p>
          )}

          <div className="space-y-1.5 pt-1.5">
            {passwordRules.map((rule) => (
              <RequirementItem
                key={rule.id}
                met={rule.test(passwordValue)}
                label={rule.label}
                testId={`password-requirement-${rule.id}`}
              />
            ))}
          </div>
        </div>

        <div className="w-full space-y-2">
          <Label
            htmlFor="password_confirmation"
            className={cn(
              'block text-sm leading-none',
              errors.password_confirmation && 'text-destructive',
            )}
          >
            Confirm new password
          </Label>
          <Input
            id="password_confirmation"
            type="password"
            placeholder="••••••••"
            className={cn(
              errors.password_confirmation && 'border-destructive focus-visible:ring-destructive',
            )}
            aria-invalid={!!errors.password_confirmation}
            aria-describedby={errors.password_confirmation ? 'password-confirmation-error' : undefined}
            {...register('password_confirmation')}
          />
          {errors.password_confirmation && (
            <p id="password-confirmation-error" className="leading-none text-destructive text-sm">
              {errors.password_confirmation.message}
            </p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={isPending}>
          {isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Updating password…
            </>
          ) : (
            'Update password'
          )}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default ResetPassword;
